import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActiveAlertsPanel = ({
  alerts,
  filter,
  onFilterChange,
  onAssign,
  onResolve,
  onEscalate,
  onBroadcast,
  staffAvailability,
  loading
}) => {
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [showAssignMenu, setShowAssignMenu] = useState(null);

  const filters = [
    { value: 'all', label: 'All Alerts', count: alerts?.length },
    { value: 'critical', label: 'Critical', icon: 'AlertCircle' },
    { value: 'high', label: 'High', icon: 'AlertTriangle' },
    { value: 'unassigned', label: 'Unassigned', icon: 'UserX' },
    { value: 'payment', label: 'Payment', icon: 'CreditCard' },
    { value: 'equipment', label: 'Equipment', icon: 'Wrench' },
    { value: 'capacity', label: 'Capacity', icon: 'Users' }
  ];

  const getSeverityColor = (severity) => {
    const colorMap = {
      critical: 'error',
      high: 'warning',
      medium: 'primary',
      low: 'success'
    };
    return colorMap?.[severity] || 'primary';
  };

  const getSeverityBg = (severity) => {
    const bgMap = {
      critical: 'bg-error/10 text-error border-error/20',
      high: 'bg-warning/10 text-warning border-warning/20',
      medium: 'bg-primary/10 text-primary border-primary/20',
      low: 'bg-success/10 text-success border-success/20'
    };
    return bgMap?.[severity] || 'bg-primary/10 text-primary border-primary/20';
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffMs = now - alertTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ago`;
  };

  const getEscalationColor = (timer) => {
    if (timer <= 15) return 'text-error';
    if (timer <= 30) return 'text-warning';
    return 'text-success';
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Active Alerts</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {alerts?.length} active {alerts?.length === 1 ? 'alert' : 'alerts'} requiring attention
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto custom-scrollbar pb-2">
        {filters?.map((filterOption) => (
          <button
            key={filterOption?.value}
            onClick={() => onFilterChange(filterOption?.value)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              filter === filterOption?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            {filterOption?.icon && <Icon name={filterOption?.icon} size={16} />}
            {filterOption?.label}
            {filterOption?.count !== undefined && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs ${
                  filter === filterOption?.value
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-foreground/10 text-foreground'
                }`}
              >
                {filterOption?.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Alerts List */}
      {alerts?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
            <Icon name="CheckCircle" size={32} className="text-success" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Active Alerts</h3>
          <p className="text-sm text-muted-foreground">All systems operational</p>
        </div>
      ) : (
        <div className="space-y-4">
          {alerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`border-l-4 rounded-lg p-4 transition-all ${
                expandedAlert === alert?.id ? 'bg-muted/50' : 'bg-background'
              } border-${getSeverityColor(alert?.severity)}`}
              style={{ borderLeftColor: `var(--color-${getSeverityColor(alert?.severity)})` }}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getSeverityBg(alert?.severity)}`}>
                  <Icon name={alert?.icon} size={24} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-base font-semibold text-foreground">{alert?.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityBg(alert?.severity)}`}>
                          {alert?.severity?.toUpperCase()}
                        </span>
                        {alert?.escalationLevel > 0 && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-error/10 text-error">
                            Level {alert?.escalationLevel}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{alert?.description}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {getTimeAgo(alert?.createdAt)}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap items-center gap-4 mb-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="MapPin" size={14} />
                      <span>{alert?.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Tag" size={14} />
                      <span>{alert?.category}</span>
                    </div>
                    {alert?.assignedTo && (
                      <div className="flex items-center gap-1">
                        <Icon name="User" size={14} />
                        <span>{alert?.assignedTo} ({alert?.assignedRole})</span>
                      </div>
                    )}
                  </div>

                  {/* Escalation Timer */}
                  <div className="mb-3 p-3 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={16} className={getEscalationColor(alert?.escalationTimer)} />
                        <span className="text-sm font-medium text-foreground">Auto-escalation in:</span>
                      </div>
                      <span className={`text-lg font-bold ${getEscalationColor(alert?.escalationTimer)}`}>
                        {alert?.escalationTimer}m
                      </span>
                    </div>
                    <div className="mt-2 h-2 bg-background rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          alert?.escalationTimer <= 15 ? 'bg-error' :
                          alert?.escalationTimer <= 30 ? 'bg-warning' : 'bg-success'
                        }`}
                        style={{ width: `${(alert?.escalationTimer / 60) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="CheckCircle"
                      iconPosition="left"
                      onClick={() => onResolve(alert?.id)}
                    >
                      Resolve
                    </Button>
                    
                    <div className="relative">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="UserPlus"
                        iconPosition="left"
                        onClick={() => setShowAssignMenu(showAssignMenu === alert?.id ? null : alert?.id)}
                      >
                        {alert?.assignedTo ? 'Reassign' : 'Assign'}
                      </Button>
                      
                      {showAssignMenu === alert?.id && (
                        <div className="absolute top-full left-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                          <div className="p-2">
                            {staffAvailability?.filter(s => s?.status !== 'offline')?.map((staff) => (
                              <button
                                key={staff?.id}
                                onClick={() => {
                                  onAssign(alert?.id, staff?.id);
                                  setShowAssignMenu(null);
                                }}
                                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                              >
                                <div className={`w-2 h-2 rounded-full ${
                                  staff?.status === 'available' ? 'bg-success' : 'bg-warning'
                                }`} />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-foreground">{staff?.name}</p>
                                  <p className="text-xs text-muted-foreground">{staff?.role} • {staff?.currentAlerts} alerts</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {alert?.escalationLevel < alert?.maxEscalationLevel && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ArrowUp"
                        iconPosition="left"
                        onClick={() => onEscalate(alert?.id)}
                      >
                        Escalate
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Radio"
                      iconPosition="left"
                      onClick={() => onBroadcast(alert?.id)}
                    >
                      Broadcast
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      iconName={expandedAlert === alert?.id ? 'ChevronUp' : 'ChevronDown'}
                      onClick={() => setExpandedAlert(expandedAlert === alert?.id ? null : alert?.id)}
                    >
                      {expandedAlert === alert?.id ? 'Less' : 'More'}
                    </Button>
                  </div>

                  {/* Expanded Details */}
                  {expandedAlert === alert?.id && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <h4 className="text-sm font-semibold text-foreground mb-3">Escalation History</h4>
                      <div className="space-y-2">
                        <div className="flex items-start gap-3 text-sm">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="Circle" size={12} className="text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="text-foreground font-medium">Alert Created</p>
                            <p className="text-xs text-muted-foreground">{getTimeAgo(alert?.createdAt)}</p>
                          </div>
                        </div>
                        {alert?.assignedTo && (
                          <div className="flex items-start gap-3 text-sm">
                            <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="UserCheck" size={12} className="text-success" />
                            </div>
                            <div className="flex-1">
                              <p className="text-foreground font-medium">Assigned to {alert?.assignedTo}</p>
                              <p className="text-xs text-muted-foreground">2 minutes ago</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveAlertsPanel;